import { cn } from '@ibas/ui';

export interface NavyAtmosphereProps {
  className?: string;
}

/**
 * NavyAtmosphere — the shared decorative backdrop for IBAS's plain-navy,
 * full-screen "statement" sections (WordHero's word-cycling hero, the closing
 * "Ready to graduate" CTA). Three static layers give the flat navy some
 * depth without competing with the white/gold copy on top of it:
 *
 *   1. a soft radial depth gradient — navy-2 core fading to navy, echoing
 *      the same recipe used on ConstellationHero / SerenityIntro;
 *   2. two very low-opacity blurred gold orbs anchored to opposite corners;
 *   3. a faint dotted-grid texture for a touch of print-grade craft.
 *
 * Using one component in both sections is what makes them read as a matched
 * pair rather than two one-off treatments. It is intentionally inert (no
 * GSAP, no listeners) — nothing here is legible motion, so it needs no
 * reduced-motion branching and can never strand or distract from content.
 * Render it first inside a `relative isolate overflow-hidden` section and
 * give the actual content wrapper `relative z-10` so it paints on top.
 */
export function NavyAtmosphere({ className }: NavyAtmosphereProps) {
  return (
    <div aria-hidden="true" className={cn('pointer-events-none absolute inset-0 z-0', className)}>
      {/* Radial depth gradient: navy-2 core fading to the flat navy base. */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,var(--color-brand-navy-2)_0%,var(--color-brand-navy)_60%)]" />

      {/* Low-opacity blurred gold orbs, off to opposite corners. */}
      <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
      <div className="absolute -right-24 -bottom-40 h-[32rem] w-[32rem] rounded-full bg-brand-gold/10 blur-3xl" />

      {/* Faint dotted-grid texture. */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
    </div>
  );
}
